import React, { useState } from "react";
import { Share2, Check, Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * Smart Share button — generates a link that goes through backend OG endpoint
 * so social-media previews (WhatsApp / Facebook / LinkedIn / Twitter) show
 * the correct title + image + description for the page being shared.
 *
 * Backend route handles the OG meta and immediately redirects the human user
 * back to the React route after the bot scrape.
 */
export default function ShareButton({ id, slug, type = "course", title, className = "" }) {
  const [copied, setCopied] = useState(false);

  const buildShareUrl = () => {
    // CRITICAL: always use the current page origin (so production builds → production URL,
    // preview builds → preview URL) instead of the build-time REACT_APP_BACKEND_URL.
    const origin = (typeof window !== "undefined" && window.location && window.location.origin) || "";
    if (type === "webinar") return `${origin}/api/og/webinar/${slug}`;
    if (type === "city") return `${origin}/api/og/city/${slug}`;
    return `${origin}/api/og/course/${id}`;
  };

  const onShare = async () => {
    const url = buildShareUrl();
    // Native share when available (mobile)
    if (navigator.share) {
      try {
        await navigator.share({ title: title || "CodeWithKamlesh", url });
        return;
      } catch (_) { /* user cancelled, fallthrough to copy */ }
    }
    // Fallback — copy
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Share link copied — paste it on WhatsApp / Facebook for rich preview");
      setTimeout(() => setCopied(false), 2400);
    } catch {
      toast.error("Could not copy — long-press the link to share");
    }
  };

  return (
    <button
      type="button"
      onClick={onShare}
      className={`w-full inline-flex items-center justify-center gap-2 bg-white/5 border border-white/15 hover:bg-white/10 text-white font-medium rounded-full px-4 py-2.5 text-sm transition ${className}`}
      data-testid={`share-${type}-btn`}
    >
      {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Share2 className="w-4 h-4 text-[#FF0066]" />}
      {copied ? "Link copied!" : "Share with rich preview"}
    </button>
  );
}
